import { defineStore } from 'pinia';
import _ from 'lodash';
import { linkify } from '@/utils/stringUtils';
import { type MessageModel, type User } from 'src/types/interfaces';

export const useMessagesStore = defineStore('messages', {
  state: () => ({
    messages: [] as MessageModel[],
    pendingMessages: [] as MessageModel[],
    careTeam: [] as User[],
    unreadMessagesCount: 0,
    isLoading: false,
    outOfOfficeMessageTitle: '',
    outOfOfficeMessageDescription: '',
    hasShownOooModal: false,
    userId: 0
  }),
  getters: {
    unreadMessages(state) {
      return _.filter(state.messages, m => m.fromId !== state.userId && m.readDate == null);
    },
    allMessages(state): MessageModel[] {
      return [...state.messages, ...state.pendingMessages];
    },
    getAvatarforClinicianUserId(state) {
      return (userId) => {
        const clinician = state.careTeam ? state.careTeam.find(x => x.userId === userId) : null;
        return clinician?.avatarBase64 ?? null;
      };
    },
    getHasShownOOOState(){
      const t = JSON.parse(sessionStorage.getItem('hasShownOooModal'));
      if (t!= null){
        this.hasShownOooModal = t;
      } else {
        this.hasShownOooModal = false;
      }

    },
  },
  actions: {
    clear() {
      this.messages = [];
      this.pendingMessages = [];
      this.careTeam = [];
      this.unreadMessagesCount = 0;
      this.isLoading = false;
      this.outOfOfficeMessageTitle = '';
      this.outOfOfficeMessageDescription = '';
      this.hasShownOooModal = false;
    },
    setIsLoading(isLoading) {
      this.isLoading = isLoading;
    },
    setMessages(messages) {
      this.messages = _.orderBy(messages, ['sortIndex'], ['desc']);
    },
    setUnreadMessagesCount(count) {
      this.unreadMessagesCount = count;
    },
    setUserId(userId: number) {
      this.userId = userId;
    },
    setOutOfOfficeMessage(outOfOfficeMessage) {
      this.outOfOfficeMessageTitle = outOfOfficeMessage?.localizedTitle || '';
      this.outOfOfficeMessageDescription = outOfOfficeMessage?.localizedDescription.length > 0 ? linkify(outOfOfficeMessage?.localizedDescription) : '';
    },
    setCareTeam(careTeam) {
      this.careTeam = careTeam;
    },
    setHasShownOooModal() {
      this.hasShownOooModal = true;
      sessionStorage.setItem('hasShownOooModal', JSON.stringify(this.hasShownOooModal));
    },
    addPendingMessage(msg: MessageModel) {
      this.pendingMessages.push(msg);
    },
    updatePendingMessageStatus(clientId: string, status: 'pending' | 'failed') {
      const msg = this.pendingMessages.find(m => m._clientId === clientId);
      if (msg) msg._status = status;
    },
    removePendingMessage(clientId: string) {
      this.pendingMessages = this.pendingMessages.filter(m => m._clientId !== clientId);
    },
    loadMessages(messageThread) {
      this.setMessages(messageThread.messages);
      // Drop any pending messages that are now confirmed in the server response.
      // The server doesn't echo our client-side _clientId, so we match by
      // (fromId, message text) and consume one server match per pending entry —
      // that way a rapid duplicate-text send (e.g. "ok" twice) still resolves
      // both pending entries against both server entries.
      if (this.pendingMessages.length > 0) {
        const serverByUser = new Map<number, string[]>();
        for (const m of messageThread.messages) {
          const list = serverByUser.get(m.fromId);
          if (list) list.push(m.message);
          else serverByUser.set(m.fromId, [m.message]);
        }
        this.pendingMessages = this.pendingMessages.filter(p => {
          const texts = serverByUser.get(p.fromId);
          if (!texts) return true;
          const idx = texts.indexOf(p.message);
          if (idx < 0) return true;
          texts.splice(idx, 1);
          return false;
        });
      }
      this.setUnreadMessagesCount(this.unreadMessages.length);
      this.setOutOfOfficeMessage(messageThread.outOfOfficeMessage);
    },
    clearUnreadMessagesCount() {
      this.setUnreadMessagesCount(0);
    },
  },
});
